import { useMemo, useCallback, useState, useEffect } from "react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";
import { load, save } from "./useLocalStorage";
import {
  useListFiltersQuery,
  useCreateFilterMutation,
  useUpdateFilterMutation,
  useDeleteFilterMutation,
} from "components/gaia-objects/gaia-objects-filter/endpoints";
import { Filter } from "components/gaia-objects/gaia-objects-filter/definitions";

interface UseFiltersProps {
  teamName: string;
  category: string;
  currentQuery: PropertyFilterProps.Query;
  skip?: boolean;
}

export const useFilters = ({
  teamName,
  category,
  currentQuery,
  skip = false,
}: UseFiltersProps) => {
  const { data: filters = [], isLoading } = useListFiltersQuery(
    { teamName, category },
    { skip: skip || !teamName },
  );
  const [createFilterMutation] = useCreateFilterMutation();
  const [updateFilterMutation] = useUpdateFilterMutation();
  const [deleteFilterMutation] = useDeleteFilterMutation();

  const storageKey = `${teamName}-${category}-ActiveFilterId`;
  const [activeFilterId, _setActiveFilterId] = useState<string | null>(
    () => load(storageKey) ?? null,
  );
  useEffect(() => {
    _setActiveFilterId(load(storageKey) ?? null);
  }, [storageKey]);
  const setActiveFilterId = useCallback(
    (id: string | null) => {
      _setActiveFilterId(id);
      save(storageKey, id);
    },
    [storageKey],
  );

  const activeFilter = useMemo(
    () => filters.find((f) => f.filterId === activeFilterId) ?? undefined,
    [filters, activeFilterId],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!activeFilter) return false;
    return JSON.stringify(currentQuery) !== activeFilter.filterQuery;
  }, [activeFilter, currentQuery]);

  const myFilters = useMemo(() => filters.filter((f) => !f.shared), [filters]);
  const teamFilters = useMemo(() => filters.filter((f) => f.shared), [filters]);

  const saveFilter = useCallback(
    async (name: string, shared: boolean) => {
      const result = await createFilterMutation({
        teamName,
        category,
        filterName: name,
        filterQuery: JSON.stringify(currentQuery),
        shared,
      }).unwrap();
      setActiveFilterId(result.filterId);
    },
    [createFilterMutation, teamName, category, currentQuery, setActiveFilterId],
  );

  const updateFilter = useCallback(
    async (
      filterId: string,
      updates: { filterName?: string; filterQuery?: string; shared?: boolean },
    ) => {
      await updateFilterMutation({ filterId, ...updates }).unwrap();
    },
    [updateFilterMutation],
  );

  const deleteFilter = useCallback(
    async (filterId: string) => {
      await deleteFilterMutation(filterId).unwrap();
      if (activeFilterId === filterId) setActiveFilterId(null);
    },
    [deleteFilterMutation, activeFilterId, setActiveFilterId],
  );

  const applyFilter = useCallback(
    (filter: Filter): PropertyFilterProps.Query => {
      setActiveFilterId(filter.filterId);
      return JSON.parse(filter.filterQuery);
    },
    [setActiveFilterId],
  );

  const clearActiveFilter = useCallback(() => {
    setActiveFilterId(null);
  }, [setActiveFilterId]);

  return {
    filters,
    myFilters,
    teamFilters,
    isLoading,
    activeFilter,
    activeFilterId,
    hasUnsavedChanges,
    saveFilter,
    updateFilter,
    deleteFilter,
    applyFilter,
    clearActiveFilter,
    setActiveFilterId,
  };
};
